import { pickPartForCloud, shrinkPartsForCloud } from "@/cloud/cloud-part-image-fit";
import type { HomeEconomy } from "@/home/economy";
import { normalizeHomeRoom, type HomeRoomData } from "@/home/storage";

export { pickPartForCloud } from "@/cloud/cloud-part-image-fit";

/** Stay under Firestore's 1 MiB document cap (proto overhead included). */
const CLOUD_DOC_BUDGET = 900_000;

export type PlayerHomeCloudPayload = {
  room: HomeRoomData | null;
  economy: HomeEconomy;
};

function utf8Size(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

function envelopeSize(payload: PlayerHomeCloudPayload): number {
  return utf8Size({ savedAt: 0, payload });
}

function pickRoomForCloud(room: HomeRoomData): HomeRoomData {
  return normalizeHomeRoom({
    ...room,
    parts: room.parts.map(pickPartForCloud),
  });
}

async function shrinkRoomImages(room: HomeRoomData, maxEdge: number): Promise<HomeRoomData> {
  const parts = await shrinkPartsForCloud(room.parts, maxEdge);
  return normalizeHomeRoom({ ...room, parts });
}

/**
 * Home cloud docs embed many part images.
 * Firestore rejects an oversized `payload` as "invalid nested entity".
 */
export async function fitHomePayloadForCloud(
  room: HomeRoomData | null,
  economy: HomeEconomy,
): Promise<PlayerHomeCloudPayload> {
  const pickedRoom = room ? pickRoomForCloud(room) : null;
  let payload: PlayerHomeCloudPayload = { room: pickedRoom, economy };
  if (envelopeSize(payload) <= CLOUD_DOC_BUDGET) return payload;

  if (!pickedRoom) return payload;

  let maxEdge = 640;
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const shrunk = await shrinkRoomImages(pickedRoom, maxEdge);
    payload = { room: shrunk, economy };
    if (envelopeSize(payload) <= CLOUD_DOC_BUDGET) return payload;
    maxEdge = Math.round(maxEdge * 0.72);
  }

  throw new Error("ข้อมูลบ้านใหญ่เกินที่คลาวด์รับได้");
}
